import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { LoginRequest, User } from '../models/user.model';
import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokens = inject(TokenService);
  private readonly router = inject(Router);

  /** Origin root of the API (drops `/api/v1`) — where Sanctum's csrf-cookie lives. */
  private readonly csrfCookieUrl = `${environment.apiUrl.replace(/\/api\/v1\/?$/, '')}/sanctum/csrf-cookie`;

  // Reactive auth state using Angular signals. Auth is carried by the httpOnly
  // session cookie (Sanctum SPA mode); the persisted user is a UX cache only,
  // validated against /auth/me on load.
  private readonly _user = signal<User | null>(this.loadUser());
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);

  /**
   * Sanctum SPA login: prime the CSRF cookie, then POST credentials. Auth is
   * established as a session cookie — no token is returned. `remember` controls
   * how long the persisted-user UX cache lives (the session cookie lifetime is
   * governed server-side by SESSION_LIFETIME).
   */
  login(payload: LoginRequest, remember = false): Observable<User> {
    return this.http.get(this.csrfCookieUrl, { withCredentials: true }).pipe(
      switchMap(() =>
        this.http.post<ApiResponse<User>>(`${environment.apiUrl}/auth/login`, {
          ...payload,
          remember_me: remember,
        })
      ),
      tap((res) => {
        if (res.success && res.data) {
          this.persistUser(res.data, remember);
        }
      }),
      map((res) => res.data!)
    );
  }

  /**
   * Request a password-reset link (spec §3.5). Primes the CSRF cookie first —
   * a visitor here is not logged in and has no XSRF-TOKEN cookie yet. The API
   * response is identical whether or not the account exists (no enumeration).
   */
  forgotPassword(email: string): Observable<void> {
    return this.http.get(this.csrfCookieUrl, { withCredentials: true }).pipe(
      switchMap(() =>
        this.http.post<ApiResponse<null>>(`${environment.apiUrl}/auth/forgot-password`, { email })
      ),
      map(() => undefined)
    );
  }

  /**
   * Complete a password reset with the emailed token. Primes CSRF first (the
   * user arrives from an email link with no session/XSRF cookie).
   */
  resetPassword(payload: {
    email: string;
    token: string;
    new_password: string;
    new_password_confirmation: string;
  }): Observable<void> {
    return this.http.get(this.csrfCookieUrl, { withCredentials: true }).pipe(
      switchMap(() =>
        this.http.post<ApiResponse<null>>(`${environment.apiUrl}/auth/reset-password`, payload)
      ),
      map(() => undefined)
    );
  }

  logout(): Observable<void> {
    return this.http.post<ApiResponse<null>>(`${environment.apiUrl}/auth/logout`, {}).pipe(
      tap(() => this.clearSession()),
      catchError((err) => {
        // Even if API errors, clear local state — server token will expire eventually.
        this.clearSession();
        return throwError(() => err);
      }),
      map(() => undefined)
    );
  }

  refreshCurrentUser(): Observable<User | null> {
    // Only probe /auth/me when we believe we're authenticated — a cached user
    // (SPA session) or a bearer token (switch-client). Avoids a 401 on every
    // anonymous page load.
    if (!this._user() && !this.tokens.hasToken()) {
      return of(null);
    }
    return this.http.get<ApiResponse<User>>(`${environment.apiUrl}/auth/me`).pipe(
      tap((res) => res.success && res.data && this.persistUser(res.data)),
      map((res) => res.data ?? null),
      catchError(() => {
        this.clearSession();
        return of(null);
      })
    );
  }

  /**
   * Adopt a session from a re-issued token (e.g. switch-client, SLS-1188) and
   * load the now-current user from /auth/me. Keeps the existing persistence mode.
   */
  adoptSession(token: string): Observable<User | null> {
    this.tokens.setToken(token, this.tokens.isPersistent());
    return this.refreshCurrentUser();
  }

  hasRole(role: string): boolean {
    return this._user()?.roles?.includes(role) ?? false;
  }

  hasPermission(permission: string): boolean {
    return this._user()?.permissions?.includes(permission) ?? false;
  }

  private persistUser(user: User, remember = this.tokens.isPersistent()): void {
    this._user.set(user);
    const [target, other] = remember
      ? [localStorage, sessionStorage]
      : [sessionStorage, localStorage];
    other.removeItem(environment.userStorageKey); // keep user co-located with token
    target.setItem(environment.userStorageKey, JSON.stringify(user));
  }

  private loadUser(): User | null {
    const raw =
      localStorage.getItem(environment.userStorageKey) ??
      sessionStorage.getItem(environment.userStorageKey);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as User;
    } catch {
      // Corrupt/tampered storage must never break app bootstrap — drop it.
      localStorage.removeItem(environment.userStorageKey);
      sessionStorage.removeItem(environment.userStorageKey);
      return null;
    }
  }

  /**
   * Clear all client-side auth state (token + persisted user + signal).
   * Shared by logout, failed refresh, and the 401 path in errorInterceptor so
   * there is a single source of truth for "we are no longer authenticated".
   */
  clearSession(redirect = true): void {
    this.tokens.clearToken();
    localStorage.removeItem(environment.userStorageKey);
    sessionStorage.removeItem(environment.userStorageKey);
    this._user.set(null);
    if (redirect) {
      this.router.navigate(['/authentication']);
    }
  }
}
